import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslations } from "@fenetech/translations";
import { FormProvider, useForm } from "react-hook-form";
import { BlankESignatureSettings } from "./BlankESignatureSettings";
import { Alert, AlertColor, Container, Snackbar, Tab, Tabs } from "@mui/material";
import ESignatureReportsGrid from "./ESignatureReportsGrid";
import { FindAllNestedProps, GetParamCaseInsensitive, isNullOrWhiteSpace } from "helpers/objects";
import useMessageBox from "helpers/context/Page/useMessageBox";
import { IESignatureReportSetting, IEsignatureSettings } from "models/IESignature";
import useActionButtons from "helpers/context/Page/useActionButtons";
import useWait from "helpers/context/Page/useWait";
import useWindowTitle from "helpers/context/Title/useWindowTitle";
import { IActionButton } from "helpers/context/Page/PageContext";
import { ThemeColorEnum } from "helpers/enums";
import ESignatureAPI from "./ESignatureAPI";
import ESignatureSelectCustomer from "./ESignatureSelectCustomer";
import { IMfgCustomer } from "models/IMfgCustomer";
import useReports from "helpers/context/SelectionValues/useReports";
import ESignatureConnection from "./ESignatureConnection";
import { useSearchParams } from "react-router-dom";
import { useEffectOnLoad } from "helpers/hooks/useEffectOnLoad";

const ESignature: React.FC = () => {

    const tm = useTranslations();
    const messageBox = useMessageBox();
    const actionButtons = useActionButtons();
    const wait = useWait();
    const { reportSelections } = useReports("web", "quote");
    const [query] = useSearchParams();

    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isEditMode, setIsEditMode] = useState<boolean>(false);
    const [alert, setAlert] = useState<[text: string, alertType: AlertColor] | null>(null);

    const [selectedMfgCustomer, setSelectedMfgCustomer] = useState<IMfgCustomer | null>(null);
    const [selectedMfgSite, setSelectedMfgSite] = useState<IMfgCustomer | null>(null);
    const [eSignatureSettings, setESignatureSettings] = useState<IEsignatureSettings>(BlankESignatureSettings);
    const [currentTab, setCurrentTab] = useState<number>(0);

    const formMethods = useForm({ mode: "onChange", defaultValues: BlankESignatureSettings});
    const { handleSubmit, reset } = formMethods;

    const authCustomerGUID: string | null = useMemo(() => {
        return GetParamCaseInsensitive(query, "customerGUID") ?? null;
    }, [query]);

    const eSignMessage: string | null = useMemo(() => {
        return GetParamCaseInsensitive(query, "eSignMessage") ?? null;
    }, [query]);

    useEffectOnLoad(() => {
        //Check if we have a customerGUID when loading in. If so, we just came from the 3rd party login and can check if it succeeded
        if (isNullOrWhiteSpace(authCustomerGUID)) {
            return;
        }

        if (isNullOrWhiteSpace(eSignMessage)) {
            setAlert([tm.Get("Sign in successful. Please retry the ping test"), "success"]);
        } else {
            setAlert([eSignMessage!, "error"]);
        }
    });

    useEffect(() => {
        wait.Show(isLoading);
    }, [isLoading, wait]);

    const title = useMemo(() => {
        const title = [tm.Get("E-Signature")];

        if (selectedMfgCustomer) {
            title.push(selectedMfgCustomer.name);
        }

        return title;
    }, [tm, selectedMfgCustomer]);

    useWindowTitle(...title);

    const resolveAvailableReports = useCallback((reportSettings: IESignatureReportSetting[]) => {
        //It's possible base reports do not have settings yet. If not, include them in this grid so they can be configured
        reportSelections.forEach(br => {
            if (!reportSettings.some(rs => rs.reportID === br.reportID.toString())) {
                let baseReportSetting: IESignatureReportSetting = {
                    reportID: br.reportID.toString(),
                    reportName: br.menuCaption,
                    anchorTag: "",
                    units: "",
                    xOffset: 0,
                    yOffset: 0
                };
                reportSettings.push(baseReportSetting);
            }
        });

        //Do not get report names from the DB since base reports are hard coded in frontend code
        reportSettings.forEach(rs => {
            if (isNullOrWhiteSpace(rs.reportName)) {
                rs.reportName = reportSelections.find(r => r.reportID.toString() === rs.reportID)?.menuCaption ?? "";
            }
        });

        return reportSettings.sort((a, b) => a.reportName.localeCompare(b.reportName));
    }, [reportSelections]);

    const getESignatureSettings = useCallback(async (mfgSite: IMfgCustomer | null) => {
        setIsLoading(true);
        try {
            if (mfgSite !== null) {
                const settings: IEsignatureSettings = await ESignatureAPI.GetESignSettings(mfgSite.customerID, mfgSite.siteID);
                if (settings) {
                    let newSettings: IEsignatureSettings = { ...settings, reportSettings: resolveAvailableReports(settings.reportSettings) }
                    setESignatureSettings(newSettings);
                    reset(newSettings);
                }
            }
        }
        finally {
            setIsLoading(false);
        }
    }, [reset, resolveAvailableReports]);

    const onSelectMfgSite = useCallback(async (mfgSite: IMfgCustomer | null) => {
        setSelectedMfgSite(mfgSite);
        await getESignatureSettings(mfgSite);
    }, [getESignatureSettings]);

    useEffect(() => {
        if (!isEditMode) {
            const editButton: IActionButton = {
                text: tm.Get("Edit"),
                color: ThemeColorEnum.Secondary,
                disabled: isLoading || !selectedMfgSite,
                onClick: (() => setIsEditMode(true))
            };

            actionButtons.Set(0, editButton);
        } else {
            const saveButton : IActionButton = {
                text: tm.Get("Save"), 
                color: ThemeColorEnum.Secondary,
                disabled: isLoading,
                type: "submit",
                form: "edit-eSignSettings"
            };

            const cancelButton : IActionButton = {
                text: tm.Get("Cancel"), 
                color: ThemeColorEnum.Primary,
                disabled: isLoading,
                onClick: () => {
                    setIsEditMode(false);
                    actionButtons.Remove(1);
                    reset(eSignatureSettings);
                }
            };
            actionButtons.Set(0, saveButton);
            actionButtons.Set(1, cancelButton);
        }
    }, [isEditMode, isLoading, actionButtons, tm, reset, eSignatureSettings, selectedMfgSite]);

    const onSubmit = useCallback(async (formData, e: any) => {

        const update: IEsignatureSettings = formData;

        update.reportSettings = update.reportSettings.map(rs => isNullOrWhiteSpace(rs.xOffset.toString()) ? {...rs, xOffset: 0} : rs);
        update.reportSettings = update.reportSettings.map(rs => isNullOrWhiteSpace(rs.yOffset.toString()) ? {...rs, yOffset: 0} : rs);

        try {
            await ESignatureAPI.UpdateESignSettings(selectedMfgSite?.customerID!, selectedMfgSite?.siteID!, update);
            await getESignatureSettings(selectedMfgSite);

            setAlert([tm.Get("Changes have been saved."), "success"]);
            setIsEditMode(false);
            actionButtons.Remove(1);
        } finally {
            setIsLoading(false);
        }
    }, [tm, actionButtons, getESignatureSettings, selectedMfgSite]);

    const onError = useCallback((errors: { [x: string]: any }, e: any) => {
        const messages = FindAllNestedProps(errors, "message");
        const formattedMessage = messages.join("\n");
        console.log(formattedMessage);

        if (formattedMessage) {
            messageBox.Show({ message: formattedMessage, title: tm.Get("Please correct before saving.") });
        }
    }, [messageBox, tm]);

    const handleClose = useCallback((event?: React.SyntheticEvent | Event, reason?: string) => {
        if (reason === 'clickaway') 
            return;
        setAlert(null);
    }, []);

    return <>
        <Snackbar open={alert !== null} autoHideDuration={5000} onClose={handleClose} >
            <Alert onClose={handleClose} severity={alert ? alert[1] : "info"} variant='filled' sx={{width: '100%', fontWeight: 'bold'}}>{alert ? alert[0] : ""}</Alert>
        </Snackbar>

        <Container maxWidth="lg" sx={{mt: 1}}>
            <FormProvider {...formMethods}>
                <form id="edit-eSignSettings" onSubmit={handleSubmit(onSubmit, onError)}>
                    <ESignatureSelectCustomer
                        isEditMode={isEditMode}
                        selectedMfgCustomer={selectedMfgCustomer}
                        selectedMfgSite={selectedMfgSite}
                        setSelectedMfgCustomer={setSelectedMfgCustomer}
                        setSelectedMfgSite={onSelectMfgSite}
                        authCustomerGUID={authCustomerGUID}
                    />
                    {selectedMfgSite && <>
                        <Tabs value={currentTab} onChange={(e, value) => setCurrentTab(value)} sx={{mb: 2}}>
                            <Tab label={tm.Get("General Setup")} />
                            <Tab label={tm.Get("Report Setup")} />
                        </Tabs>

                        {currentTab === 0 &&
                            <ESignatureConnection 
                                isEditMode={isEditMode}
                                mfgCustomerID={selectedMfgSite.customerID}
                                mfgSiteID={selectedMfgSite.siteID}
                                setIsLoading={setIsLoading}
                                setAlert={setAlert}
                                authCustomerGUID={authCustomerGUID}
                            />
                        }
                        {currentTab === 1 &&
                            <ESignatureReportsGrid
                                isEditMode={isEditMode}
                            />
                        }
                    </>}
                </form>
            </FormProvider>
        </Container>
    </>;
}

export default ESignature;